import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  Box,
  CompassColor,
  Heading,
  Image,
  Spacing,
  Stack,
  Text,
  useMediaQuery,
} from "@noom/wax-component-library";

import { NAMESPACES } from "@/app/i18n";
import { ASSET_URL } from "@/constants";
import { useQueryParams } from "@/hooks";
import { getDisplayName, type Partner } from "@/models";
import { isHighmarkPartner } from "@/utils/partners";

type ValueProp = {
  alt: string;
  captionKey: string;
  url: string;
};

const STATIC_CONTENT_IMAGES: ValueProp[] = [
  {
    alt: "Hike",
    captionKey: "enrollmentStaticContent.valueProp1",
    url: "/assets/img/hike.png",
  },
  {
    alt: "Swim",
    captionKey: "enrollmentStaticContent.valueProp2",
    url: "/assets/img/swim.png",
  },
  {
    alt: "Stretch",
    captionKey: "enrollmentStaticContent.valueProp3",
    url: "/assets/img/stretch.png",
  },
];

type EnrollmentStaticContentProps = {
  partnerInfo: Partner | null;
};

const NoomLogoWhite: React.FC = () => (
  <Image alt="Noom" height="24px" src="/assets/img/noom-logo-white.svg" />
);

const LogoHeader: React.FC<{
  partnerInfo: Partner | null;
}> = ({ partnerInfo }) => {
  const { employerSlug } = useParams<AppParams>();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const partnerName = getDisplayName(partnerInfo);
  const partnerSlug = employerSlug || partnerInfo?.slug;
  const showOriginalLogo = partnerInfo?.configs.MAINTAIN_ORIGINAL_LOGO;

  const imageUrl = `${ASSET_URL}/img/${partnerSlug}/logo.png`;

  /**
   * Highmark specifies that their partner logo should be displayed without the
   * Noom logo. The Noom logo is displayed in the footer on desktop.
   */
  if (isHighmarkPartner(partnerInfo)) {
    return (
      <Box display="flex" gap="var(--spacing-m)">
        <Image alt={partnerName} height="24px" src={imageUrl} />
      </Box>
    );
  }

  return (
    <Box display="flex" gap="var(--spacing-m)">
      <NoomLogoWhite />
      {partnerSlug && (
        <>
          <Box
            backgroundColor={CompassColor.white}
            display={isImageLoaded ? "block" : "none"}
            height="24px"
            width="2px"
          />
          <Image
            alt={partnerName}
            filter={showOriginalLogo ? "none" : "brightness(0) invert(1)"}
            height="24px"
            onLoad={() => setIsImageLoaded(true)}
            opacity={isImageLoaded ? 1 : 0}
            src={imageUrl}
            transition="opacity 0.1s ease-in-out"
          />
        </>
      )}
    </Box>
  );
};

export const EnrollmentStaticContent: React.FC<
  EnrollmentStaticContentProps
> = ({ partnerInfo }) => {
  const [isBrowser, isTallerThan700px] = useMediaQuery([
    "(min-width: 1160px)",
    "(min-height: 700px)",
  ]);
  const { employerSlug } = useParams<AppParams>();
  const { upid } = useQueryParams();
  const { t } = useTranslation(NAMESPACES.employerLanding);

  const showLogoHeader = !(employerSlug || upid) || !!partnerInfo;
  const showLogoFooter = isBrowser && isHighmarkPartner(partnerInfo);
  const showValueProps = isBrowser && isTallerThan700px;

  return (
    <Box
      color={CompassColor.white}
      display="flex"
      flexDirection="column"
      minHeight={isBrowser ? "calc(100vh - var(--spacing-xxl))" : "none"}
    >
      <Box alignItems="center" display="flex" height="36px">
        {showLogoHeader && <LogoHeader partnerInfo={partnerInfo} />}
      </Box>
      <Text
        as="p"
        color={CompassColor.sand02}
        fontFamily="Brown LL"
        fontSize="18px"
        fontWeight={500}
        lineHeight="21px"
        marginTop={{ base: "var(--spacing-l)", lg: "var(--spacing-xxxh)" }}
        textTransform="uppercase"
      >
        {t("enrollmentStaticContent.heading")}
      </Text>
      <Heading
        fontSize={{ base: "24px", lg: "40px" }}
        fontWeight={500}
        lineHeight={{ base: "32px", lg: "50px" }}
        marginBottom={{ base: "var(--spacing-xl)", lg: "var(--spacing-h)" }}
        marginTop={{ base: "var(--spacing-s)", lg: "var(--spacing-m)" }}
      >
        {t("enrollmentStaticContent.description")}
      </Heading>
      {showValueProps && (
        <Stack isInline spacing={Spacing[8]}>
          {STATIC_CONTENT_IMAGES.map(({ alt, captionKey, url }) => (
            <Stack key={url} maxWidth="192px" spacing={Spacing[4]}>
              <Image alt={alt} src={url} width="96px" />
              <Box fontFamily="Untitled Sans" fontSize="20px" lineHeight="28px">
                <Trans
                  components={{
                    boldText: (
                      <Text color={CompassColor.white} fontWeight={500} />
                    ),
                  }}
                  i18nKey={captionKey}
                  ns={NAMESPACES.employerLanding}
                />
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
      {showLogoFooter && (
        <Box marginTop="auto" marginBottom="var(--spacing-xh)">
          <NoomLogoWhite />
        </Box>
      )}
    </Box>
  );
};
